/**
 * QuickAddContactModal — small modal that turns an unmatched inbound
 * phone number into a Contact in one click. Popped from the inbox
 * list (iter 497) so operators don't have to leave the message list
 * to name a caller.
 *
 * Props
 * -----
 *   open       — controls visibility
 *   phone      — pre-populated E.164 (or raw) phone from the message thread
 *   defaultName — optional "First Last" seed (e.g. from a Twilio caller-name lookup)
 *   onClose(saved?) — closes the modal; if saved is truthy the caller should
 *                     refresh so the newly-created row shows up
 */
import { useEffect, useState } from "react";
import { X as XIcon, UserPlus, Loader2, Mail, Link2, Trash2, AlertTriangle, Search, User, Camera } from "lucide-react";
import { toast } from "sonner";
import { formatPhoneNumber, fileUrl } from "../../lib/utils";
import { RELATIONSHIP_OPTIONS } from "../../lib/contactRelationships";

const API = process.env.REACT_APP_BACKEND_URL;

const CONTACT_TYPES = [
  { value: "client",     label: "Client / Patient" },
  { value: "family",     label: "Family / Contact" },
  { value: "employee",   label: "Employee / PCW" },
  { value: "provider",   label: "Provider (MD/RN)" },
  { value: "clinic",     label: "Clinic / Facility" },
  { value: "org",        label: "Vendor / Org" },
  { value: "other",      label: "Other" },
];

function LinkedClientRow({ lc, onChangeRel, onRemove }) {
  const isPreset = lc.relationship && RELATIONSHIP_OPTIONS.includes(lc.relationship);
  const [showOther, setShowOther] = useState(!!(lc.relationship && !isPreset));
  const [other, setOther] = useState(lc.relationship && !isPreset ? lc.relationship : "");
  const selectVal = showOther ? "__other__" : (isPreset ? lc.relationship : "");

  const handleSelect = (v) => {
    if (v === "__other__") { setShowOther(true); return; }
    setShowOther(false); setOther("");
    onChangeRel({ id: lc.id, name: lc.name }, v);
  };
  const commitOther = () => {
    const val = other.trim();
    if (val) onChangeRel({ id: lc.id, name: lc.name }, val);
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={`linked-client-${lc.id}`}>
      <Link2 className="w-3 h-3 text-slate-400 shrink-0" />
      <span className="min-w-0 flex-1 text-[12px] font-semibold text-slate-700 truncate" title={lc.name}>{lc.name || "Client"}</span>
      {showOther ? (
        <input
          autoFocus value={other} onChange={(e) => setOther(e.target.value)}
          onBlur={commitOther} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitOther(); } }}
          placeholder="Relationship"
          className="w-28 px-2 py-1 rounded-md border text-[11px] focus:outline-none focus:ring-2 focus:ring-[#143875]/40"
          data-testid={`rel-custom-${lc.id}`}
        />
      ) : (
        <select
          value={selectVal} onChange={(e) => handleSelect(e.target.value)}
          className="w-32 px-2 py-1 rounded-md border text-[11px] bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#143875]/40"
          data-testid={`rel-select-${lc.id}`}
        >
          <option value="">Relationship…</option>
          {RELATIONSHIP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value="__other__">Other…</option>
        </select>
      )}
      <button onClick={() => onRemove(lc.id)} title="Remove link" data-testid={`unlink-client-${lc.id}`}
        className="w-5 h-5 rounded-md hover:bg-slate-200 flex items-center justify-center shrink-0">
        <Trash2 className="w-3 h-3 text-slate-500" />
      </button>
    </div>
  );
}

export default function QuickAddContactModal({ open, phone, contactId = null, defaultName = "", defaultType = "", editing = false, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [contactType, setContactType] = useState("family");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [dup, setDup] = useState(null);
  const [merging, setMerging] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const tok = typeof window !== "undefined" ? localStorage.getItem("token") : "";
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${tok}` };
  const notifyContactUpdated = () => { try { window.dispatchEvent(new Event("avida:contact-updated")); } catch { /* noop */ } };

  // Seed fields when the modal opens; in edit mode also pull the full contact,
  // its linked clients, and any safe duplicate candidate.
  useEffect(() => {
    if (!open) return;
    const parts = (defaultName || "").trim().split(/\s+/);
    setFirstName(parts[0] || "");
    setLastName(parts.slice(1).join(" ") || "");
    setContactType(defaultType || "family");
    setEmail(""); setNote(""); setLinked([]); setClientQ(""); setClientResults([]); setDup(null); setAvatarUrl("");
    if (editing && contactId) {
      fetch(`${API}/api/contacts/${contactId}`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : null))
        .then((c) => { if (c) { setEmail(c.email || ""); setNote(c.notes || ""); setAvatarUrl(c.avatar_url || ""); } }).catch(() => {});
      fetch(`${API}/api/contacts/${contactId}/clients`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : { linked_clients: [] }))
        .then((d) => setLinked(d.linked_clients || [])).catch(() => {});
      fetch(`${API}/api/contacts/${contactId}/duplicate`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : { duplicate: null }))
        .then((d) => setDup(d.duplicate || null)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone, defaultName, defaultType, editing, contactId]);

  const searchClients = async (q) => {
    setClientQ(q);
    if (q.trim().length < 2) { setClientResults([]); return; }
    try {
      const r = await fetch(`${API}/api/healthcare/patients?search=${encodeURIComponent(q)}&limit=6`, { headers: authHeaders });
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.patients || d.items || []);
      setClientResults(list.map((p) => ({ id: p.id, name: `${p.first_name || ""} ${p.last_name || ""}`.trim() })));
    } catch { setClientResults([]); }
  };
  const linkClient = async (c, relationship = "") => {
    try {
      await fetch(`${API}/api/contacts/${contactId}/link-client`, { method: "POST", headers: authHeaders, body: JSON.stringify({ client_id: c.id, relationship }) });
      setLinked((prev) => {
        if (prev.some((x) => x.id === c.id)) return prev.map((x) => (x.id === c.id ? { ...x, relationship } : x));
        return [...prev, { ...c, relationship }];
      });
      setClientQ(""); setClientResults([]);
      notifyContactUpdated();
    } catch { toast.error("Could not link client"); }
  };
  const unlinkClient = async (id) => {
    try {
      await fetch(`${API}/api/contacts/${contactId}/link-client/${id}`, { method: "DELETE", headers: authHeaders });
      setLinked((prev) => prev.filter((x) => x.id !== id));
      notifyContactUpdated();
    } catch { toast.error("Could not remove link"); }
  };
  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/api/contacts/${contactId}/photo`, { method: "POST", headers: { Authorization: `Bearer ${tok}` }, body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "upload failed");
      const d = await r.json();
      setAvatarUrl(d.avatar_url || "");
      notifyContactUpdated();
      toast.success("Photo updated.");
    } catch (err) { toast.error(String(err?.message || "Could not upload photo")); }
    finally { setUploadingPhoto(false); if (e.target) e.target.value = ""; }
  };
  const removePhoto = async () => {
    try {
      await fetch(`${API}/api/contacts/${contactId}/photo`, { method: "DELETE", headers: authHeaders });
      setAvatarUrl("");
      notifyContactUpdated();
    } catch { toast.error("Could not remove photo"); }
  };
  const doMerge = async () => {
    if (!dup) return;
    setMerging(true);
    try {
      const r = await fetch(`${API}/api/contacts/${contactId}/merge`, { method: "POST", headers: authHeaders, body: JSON.stringify({ other_id: dup.id }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "merge failed");
      toast.success("Duplicate merged.");
      onClose?.(true);
    } catch (e) { toast.error(String(e.message || "Could not merge")); }
    finally { setMerging(false); }
  };

  if (!open) return null;

  const save = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      toast.error("Enter at least a first or last name.");
      return;
    }
    setSaving(true);
    try {
      const tok = localStorage.getItem("token");
      // If a placeholder contact already exists for this phone (e.g.
      // an SMS-auto-import stub with no name) PATCH it in-place —
      // POSTing a duplicate would leave the phantom behind and the
      // inbox would still show a bare phone number until someone
      // manually merged them.
      const isUpdate = !!contactId;
      const url = isUpdate
        ? `${API}/api/contacts/${contactId}`
        : `${API}/api/contacts`;
      const method = isUpdate ? "PATCH" : "POST";
      const body = isUpdate
        ? {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            contact_type: contactType,
            email: email.trim(),
            notes: note,
          }
        : {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            phone_number: phone,
            contact_type: contactType,
            email: email.trim(),
            notes: note,
          };
      const r = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${r.status}`);
      }
      toast.success(isUpdate ? "Contact updated." : "Contact added.");
      notifyContactUpdated();
      onClose?.(true);
    } catch (e) {
      toast.error(String(e?.message || "Could not save contact"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center px-4"
      onClick={() => onClose?.(false)}
      data-testid="quick-add-contact-modal"
    >
      <div
        className="bg-white rounded-xl w-full max-w-sm shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-800">{editing ? "Edit contact" : "Add contact"}</div>
          </div>
          <button
            onClick={() => onClose?.(false)}
            className="h-7 w-7 rounded-md hover:bg-slate-200 flex items-center justify-center"
            title="Close"
            data-testid="quick-add-contact-close"
          >
            <XIcon className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {editing && contactId && (
            <div className="flex items-center gap-3" data-testid="contact-photo-row">
              <div className="w-14 h-14 rounded-full overflow-hidden grid place-items-center shrink-0 border border-slate-200 bg-slate-100">
                {avatarUrl
                  ? <img src={fileUrl(avatarUrl)} alt="" className="w-full h-full object-cover" data-testid="contact-photo-img" />
                  : (firstName || lastName)
                    ? <span className="text-sm font-bold text-slate-500">{`${(firstName[0] || "")}${(lastName[0] || "")}`.toUpperCase() || "?"}</span>
                    : <User className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex flex-col items-start gap-1">
                <label
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[12px] font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"
                  data-testid="contact-photo-upload"
                >
                  {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {uploadingPhoto ? "Uploading…" : (avatarUrl ? "Change photo" : "Add photo")}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onPhotoPick} disabled={uploadingPhoto} />
                </label>
                {avatarUrl && (
                  <button onClick={removePhoto} className="text-[11px] font-semibold text-red-600 hover:underline" data-testid="contact-photo-remove">
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          )}

          {editing && dup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5" data-testid="contact-duplicate-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-amber-900 font-semibold">Possible duplicate</p>
                  <p className="text-[11px] text-amber-800 truncate">{dup.name || "Unnamed contact"} shares this number.</p>
                </div>
                <button
                  onClick={doMerge} disabled={merging}
                  data-testid="contact-merge-btn"
                  className="shrink-0 h-7 px-2.5 rounded-md text-[11px] font-bold text-white disabled:opacity-50"
                  style={{ background: "#B45309" }}
                >
                  {merging ? "Merging…" : "Merge"}
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</label>
            <div
              className="mt-1 px-3 py-2 rounded-md border bg-slate-50 text-sm font-mono text-slate-700"
              data-testid="quick-add-contact-phone"
            >
              {formatPhoneNumber(phone)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">First name</label>
              <input
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#143875]/40"
                placeholder="Jane"
                data-testid="quick-add-contact-first-name"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#143875]/40"
                placeholder="Doe"
                data-testid="quick-add-contact-last-name"
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Type</label>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#143875]/40"
              data-testid="quick-add-contact-type"
            >
              {CONTACT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Email</label>
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-md border focus-within:ring-2 focus-within:ring-[#143875]/40">
              <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full text-sm focus:outline-none" placeholder="jane@example.com"
                data-testid="quick-add-contact-email"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Note</label>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#143875]/40 resize-none"
              placeholder="Anything useful about this contact…"
              data-testid="quick-add-contact-note"
            />
          </div>

          {editing && contactId && (
            <div data-testid="quick-add-contact-linked">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Linked clients
              </label>
              {linked.length > 0 && (
                <div className="mt-1 space-y-1">
                  {linked.map((lc) => (
                    <LinkedClientRow key={lc.id} lc={lc} onChangeRel={linkClient} onRemove={unlinkClient} />
                  ))}
                </div>
              )}
              <div className="mt-1.5 relative">
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border focus-within:ring-2 focus-within:ring-[#143875]/40">
                  <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <input
                    value={clientQ} onChange={(e) => searchClients(e.target.value)}
                    className="w-full text-sm focus:outline-none" placeholder="Search a client to link…"
                    data-testid="link-client-search"
                  />
                </div>
                {clientResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="link-client-results">
                    {clientResults.map((c) => (
                      <button key={c.id} onClick={() => linkClient(c)} data-testid={`link-client-opt-${c.id}`}
                        className="w-full text-left px-3 py-2 text-[12px] hover:bg-slate-50">
                        {c.name || "Client"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={() => onClose?.(false)}
            className="px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-200"
            data-testid="quick-add-contact-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: "#143875" }}
            data-testid="quick-add-contact-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : (editing ? "Save changes" : "Add contact")}
          </button>
        </div>
      </div>
    </div>
  );
}
